/**
 * "Is this id the signed-in user?" — one implementation, because getting it
 * wrong is expensive and it has already cost us once.
 *
 * **The session is known by several identifiers and they do not look alike.** The
 * token's `user_id` is a decimal string ("2"); a job's `assignee_id` is a
 * hyphenated uuid; the token carries that same uuid **unhyphenated**; and the
 * `presence` frame returns it unhyphenated too (verified against staging
 * 2026-07-29). Comparing the wrong one — or the right one without normalising —
 * made a transcriber's own claimed job read as someone else's and locked them out
 * of the form they were sent to fill in.
 *
 * Lives in `lib/` rather than beside either caller because both the workspace
 * (ownership → editability) and the api layer (presence → "who *else* is here")
 * need it, and two copies of this rule would drift.
 */
function normalise(value: string): string {
  return value.replace(/-/g, '').toLowerCase()
}

/**
 * Does `candidate` name the same user as any of `identifiers`?
 *
 * Being permissive is the safe direction here: a false negative locks someone out
 * of their own work, while a false positive would need two users with colliding
 * identifiers, which these formats do not admit.
 */
export function matchesIdentifier(
  candidate: string | null | undefined,
  identifiers: readonly string[],
): boolean {
  if (candidate === null || candidate === undefined || candidate.trim() === '') return false
  const target = normalise(candidate)
  return identifiers.some((id) => id.trim() !== '' && normalise(id) === target)
}
